import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Shield,
  ShieldCheck,
  ShieldX,
  FileText,
  Users,
  Activity,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  ArrowRight,
  RefreshCw,
  Lock,
  Unlock,
} from 'lucide-react';
import { api, endpoints } from '@/lib/api-client';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_dashboard/admin/org/security')({
  component: SecurityDashboardPage,
});

interface PolicyStats {
  cedar: {
    total: number;
    active: number;
    permit: number;
    forbid: number;
  };
  casbin: {
    totalPolicies: number;
    totalRoleAssignments: number;
    roles: number;
  };
}

interface AuthorizationStats {
  total: number;
  allowed: number;
  denied: number;
  allowRate: number;
  avgEvaluationMs: number;
  trend: 'up' | 'down' | 'stable';
  trendPercentage: number;
}

interface TopDeniedResource {
  resourceType: string;
  resourceId: string;
  action: string;
  denyCount: number;
  lastDeniedAt: string;
}

interface UserAccessSummary {
  userId: string;
  userEmail: string;
  totalRequests: number;
  deniedRequests: number;
  lastActivity: string;
}

interface ComplianceStatus {
  name: string;
  status: 'compliant' | 'warning' | 'non-compliant';
  description: string;
  lastChecked: string;
}

function SecurityDashboardPage() {
  const [policyStats, setPolicyStats] = useState<PolicyStats | null>(null);
  const [authStats, setAuthStats] = useState<AuthorizationStats | null>(null);
  const [topDenied, setTopDenied] = useState<TopDeniedResource[]>([]);
  const [userAccess, setUserAccess] = useState<UserAccessSummary[]>([]);
  const [compliance, setCompliance] = useState<ComplianceStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    try {
      // Fetch all data in parallel
      const [cedarPolicies, casbinPolicies] = await Promise.all([
        api.get<Array<{ id: string; effect: string; is_active: boolean }>>(endpoints.adminCedarPolicies).catch(() => []),
        api.get<Array<{ id: string; ptype: string; v0?: string; v1?: string; v2?: string }>>(endpoints.adminCasbinPolicies).catch(() => []),
      ]);

      // Calculate policy stats
      const cedarActive = cedarPolicies.filter((p) => p.is_active);
      const stats: PolicyStats = {
        cedar: {
          total: cedarPolicies.length,
          active: cedarActive.length,
          permit: cedarActive.filter((p) => p.effect === 'permit').length,
          forbid: cedarActive.filter((p) => p.effect === 'forbid').length,
        },
        casbin: {
          totalPolicies: casbinPolicies.filter((p) => p.ptype === 'p').length,
          totalRoleAssignments: casbinPolicies.filter((p) => p.ptype === 'g').length,
          roles: new Set(
            casbinPolicies
              .filter((p) => p.ptype === 'g' || p.ptype === 'p')
              .map((p) => p.v1)
              .filter(Boolean)
          ).size,
        },
      };
      setPolicyStats(stats);

      // Mock authorization stats (in production, fetch from audit log)
      setAuthStats({
        total: 15420,
        allowed: 14890,
        denied: 530,
        allowRate: 96.6,
        avgEvaluationMs: 2.3,
        trend: 'up',
        trendPercentage: 3.2,
      });

      // Mock top denied resources
      setTopDenied([
        {
          resourceType: 'Agent',
          resourceId: 'agent-prod-001',
          action: 'delete',
          denyCount: 45,
          lastDeniedAt: new Date(Date.now() - 3600000).toISOString(),
        },
        {
          resourceType: 'Secret',
          resourceId: 'api-key-vault',
          action: 'read',
          denyCount: 32,
          lastDeniedAt: new Date(Date.now() - 7200000).toISOString(),
        },
        {
          resourceType: 'Workflow',
          resourceId: 'payment-flow',
          action: 'execute',
          denyCount: 18,
          lastDeniedAt: new Date(Date.now() - 14400000).toISOString(),
        },
        {
          resourceType: 'Connection',
          resourceId: 'salesforce-prod',
          action: 'update',
          denyCount: 12,
          lastDeniedAt: new Date(Date.now() - 28800000).toISOString(),
        },
        {
          resourceType: 'User',
          resourceId: 'admin-settings',
          action: 'write',
          denyCount: 8,
          lastDeniedAt: new Date(Date.now() - 43200000).toISOString(),
        },
      ]);

      // Mock user access summary
      setUserAccess([
        {
          userId: 'u-001',
          userEmail: 'alice@example.com',
          totalRequests: 2450,
          deniedRequests: 12,
          lastActivity: new Date(Date.now() - 300000).toISOString(),
        },
        {
          userId: 'u-002',
          userEmail: 'bob@example.com',
          totalRequests: 1890,
          deniedRequests: 45,
          lastActivity: new Date(Date.now() - 600000).toISOString(),
        },
        {
          userId: 'u-003',
          userEmail: 'carol@example.com',
          totalRequests: 1234,
          deniedRequests: 8,
          lastActivity: new Date(Date.now() - 1800000).toISOString(),
        },
        {
          userId: 'u-004',
          userEmail: 'dave@example.com',
          totalRequests: 987,
          deniedRequests: 156,
          lastActivity: new Date(Date.now() - 3600000).toISOString(),
        },
        {
          userId: 'u-005',
          userEmail: 'eve@example.com',
          totalRequests: 756,
          deniedRequests: 3,
          lastActivity: new Date(Date.now() - 7200000).toISOString(),
        },
      ]);

      // Compliance status
      setCompliance([
        {
          name: 'Organization Isolation',
          status: 'compliant',
          description: 'All resources properly scoped to organizations',
          lastChecked: new Date().toISOString(),
        },
        {
          name: 'Role Assignments',
          status: 'compliant',
          description: 'All users have appropriate role assignments',
          lastChecked: new Date().toISOString(),
        },
        {
          name: 'Sensitive Field Protection',
          status: 'warning',
          description: '2 fields may need additional protection policies',
          lastChecked: new Date().toISOString(),
        },
        {
          name: 'Audit Logging',
          status: 'compliant',
          description: 'All authorization decisions are being logged',
          lastChecked: new Date().toISOString(),
        },
        {
          name: 'Policy Coverage',
          status: cedarPolicies.length >= 3 ? 'compliant' : 'warning',
          description:
            cedarPolicies.length >= 3
              ? 'Adequate policy coverage for all resource types'
              : 'Consider adding more Cedar policies for complete coverage',
          lastChecked: new Date().toISOString(),
        },
      ]);
    } catch (error) {
      console.error('Failed to fetch security dashboard data:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchDashboardData();
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const complianceScore = compliance.length > 0
    ? Math.round(
        (compliance.filter((c) => c.status === 'compliant').length / compliance.length) * 100
      )
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Security Dashboard</h1>
          <p className="text-muted-foreground">
            Overview of authorization policies, decisions, and compliance status
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshCw className={cn('h-4 w-4 mr-2', isRefreshing && 'animate-spin')} />
            Refresh
          </Button>
          <Badge
            variant="outline"
            className="px-3 py-1 bg-green-50 text-green-700 border-green-200"
          >
            <ShieldCheck className="w-3 h-3 mr-1" /> {complianceScore}% Compliant
          </Badge>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Cedar Policies */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Cedar Policies</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{policyStats?.cedar.active || 0}</div>
            <div className="flex gap-2 mt-2">
              <Badge variant="secondary" className="bg-green-50 text-green-700">
                {policyStats?.cedar.permit || 0} permit
              </Badge>
              <Badge variant="secondary" className="bg-red-50 text-red-700">
                {policyStats?.cedar.forbid || 0} forbid
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Casbin Roles */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">RBAC Configuration</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{policyStats?.casbin.roles || 0} roles</div>
            <p className="text-xs text-muted-foreground mt-1">
              {policyStats?.casbin.totalRoleAssignments || 0} user assignments
            </p>
          </CardContent>
        </Card>

        {/* Authorization Rate */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Allow Rate</CardTitle>
            {authStats?.trend === 'up' ? (
              <TrendingUp className="h-4 w-4 text-green-500" />
            ) : (
              <TrendingDown className="h-4 w-4 text-red-500" />
            )}
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{authStats?.allowRate || 0}%</div>
            <div className="flex items-center gap-1 mt-1">
              <span
                className={cn(
                  'text-xs',
                  authStats?.trend === 'up' ? 'text-green-600' : 'text-red-600'
                )}
              >
                {authStats?.trend === 'up' ? '+' : '-'}
                {authStats?.trendPercentage}%
              </span>
              <span className="text-xs text-muted-foreground">vs last week</span>
            </div>
          </CardContent>
        </Card>

        {/* Denied Requests */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Denied Requests</CardTitle>
            <ShieldX className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{authStats?.denied || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {authStats?.total.toLocaleString()} total requests (7d)
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">
            <Activity className="w-4 h-4 mr-2" /> Overview
          </TabsTrigger>
          <TabsTrigger value="denied">
            <ShieldX className="w-4 h-4 mr-2" /> Top Denied
          </TabsTrigger>
          <TabsTrigger value="users">
            <Users className="w-4 h-4 mr-2" /> User Access
          </TabsTrigger>
          <TabsTrigger value="compliance">
            <CheckCircle className="w-4 h-4 mr-2" /> Compliance
          </TabsTrigger>
        </TabsList>

        {/* OVERVIEW TAB */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Authorization Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Authorization Decisions (7 Days)</CardTitle>
                <CardDescription>Distribution of allow vs deny decisions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="flex items-center gap-1">
                        <Unlock className="h-3 w-3 text-green-500" /> Allowed
                      </span>
                      <span className="font-medium">
                        {authStats?.allowed.toLocaleString()} ({authStats?.allowRate}%)
                      </span>
                    </div>
                    <Progress value={authStats?.allowRate || 0} className="h-3 bg-gray-100" />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="flex items-center gap-1">
                        <Lock className="h-3 w-3 text-red-500" /> Denied
                      </span>
                      <span className="font-medium">
                        {authStats?.denied.toLocaleString()} (
                        {(100 - (authStats?.allowRate || 0)).toFixed(1)}%)
                      </span>
                    </div>
                    <Progress
                      value={100 - (authStats?.allowRate || 0)}
                      className="h-3 bg-gray-100 [&>div]:bg-red-500"
                    />
                  </div>
                </div>
                <div className="pt-4 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Avg. Evaluation Time</span>
                    <span className="font-medium">{authStats?.avgEvaluationMs}ms</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Security Quick Actions</CardTitle>
                <CardDescription>Common security management tasks</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <Button variant="outline" className="h-auto py-4 flex-col gap-2" asChild>
                    <Link to="/admin/org/policies">
                      <FileText className="h-5 w-5" />
                      <span className="text-xs">Manage Policies</span>
                    </Link>
                  </Button>
                  <Button variant="outline" className="h-auto py-4 flex-col gap-2" asChild>
                    <Link to="/admin/org/roles">
                      <Shield className="h-5 w-5" />
                      <span className="text-xs">Manage Roles</span>
                    </Link>
                  </Button>
                  <Button variant="outline" className="h-auto py-4 flex-col gap-2" asChild>
                    <Link to="/admin/audit">
                      <Activity className="h-5 w-5" />
                      <span className="text-xs">Audit Logs</span>
                    </Link>
                  </Button>
                  <Button variant="outline" className="h-auto py-4 flex-col gap-2" asChild>
                    <Link to="/admin/org/sso">
                      <Lock className="h-5 w-5" />
                      <span className="text-xs">SSO Settings</span>
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Denied Actions Preview */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Recent Denied Actions</CardTitle>
                  <CardDescription>Latest authorization denials</CardDescription>
                </div>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/admin/audit">
                    View All <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {topDenied.slice(0, 3).map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-100"
                  >
                    <div className="flex items-center gap-3">
                      <ShieldX className="h-5 w-5 text-red-500" />
                      <div>
                        <p className="font-medium text-sm">
                          {item.action} on {item.resourceType}
                        </p>
                        <p className="text-xs text-muted-foreground">{item.resourceId}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant="destructive">{item.denyCount} denials</Badge>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatTimeAgo(item.lastDeniedAt)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TOP DENIED TAB */}
        <TabsContent value="denied" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Top Denied Resources</CardTitle>
              <CardDescription>
                Resources with the most authorization denials in the last 7 days
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Resource</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead className="text-right">Deny Count</TableHead>
                    <TableHead className="text-right">Last Denied</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topDenied.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{item.resourceType}</p>
                          <p className="text-xs text-muted-foreground">{item.resourceId}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{item.action}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="destructive">{item.denyCount}</Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">
                        {formatTimeAgo(item.lastDeniedAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* USER ACCESS TAB */}
        <TabsContent value="users" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>User Access Summary</CardTitle>
              <CardDescription>Authorization activity by user (last 7 days)</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead className="text-right">Total Requests</TableHead>
                    <TableHead className="text-right">Denied</TableHead>
                    <TableHead className="text-right">Deny Rate</TableHead>
                    <TableHead className="text-right">Last Activity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {userAccess.map((user) => {
                    const denyRate = ((user.deniedRequests / user.totalRequests) * 100).toFixed(1);
                    const isHighDenyRate = parseFloat(denyRate) > 10;
                    return (
                      <TableRow key={user.userId}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center">
                              <Users className="h-4 w-4 text-slate-500" />
                            </div>
                            <span className="font-medium">{user.userEmail}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {user.totalRequests.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={isHighDenyRate ? 'text-red-600 font-medium' : ''}>
                            {user.deniedRequests}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge
                            variant={isHighDenyRate ? 'destructive' : 'secondary'}
                            className={!isHighDenyRate ? 'bg-green-50 text-green-700' : ''}
                          >
                            {denyRate}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">
                          {formatTimeAgo(user.lastActivity)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* COMPLIANCE TAB */}
        <TabsContent value="compliance" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Compliance Status</CardTitle>
                  <CardDescription>Security policy compliance checks</CardDescription>
                </div>
                <Badge
                  variant="outline"
                  className={cn(
                    'px-3 py-1',
                    complianceScore >= 80
                      ? 'bg-green-50 text-green-700 border-green-200'
                      : complianceScore >= 50
                        ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                        : 'bg-red-50 text-red-700 border-red-200'
                  )}
                >
                  {complianceScore}% Compliant
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {compliance.map((item, index) => (
                  <div
                    key={index}
                    className={cn(
                      'flex items-center justify-between p-4 rounded-lg border',
                      item.status === 'compliant'
                        ? 'bg-green-50 border-green-200'
                        : item.status === 'warning'
                          ? 'bg-yellow-50 border-yellow-200'
                          : 'bg-red-50 border-red-200'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      {item.status === 'compliant' ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : item.status === 'warning' ? (
                        <AlertTriangle className="h-5 w-5 text-yellow-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-600" />
                      )}
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge
                        variant={
                          item.status === 'compliant'
                            ? 'default'
                            : item.status === 'warning'
                              ? 'secondary'
                              : 'destructive'
                        }
                        className={
                          item.status === 'compliant' ? 'bg-green-600 hover:bg-green-700' : ''
                        }
                      >
                        {item.status === 'compliant'
                          ? 'Compliant'
                          : item.status === 'warning'
                            ? 'Warning'
                            : 'Non-Compliant'}
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1 flex items-center justify-end gap-1">
                        <Clock className="h-3 w-3" />
                        {formatTimeAgo(item.lastChecked)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
